import React, { useId, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { HOME_FAQ_ITEMS, HOME_FAQ_SHORT } from '../data/homeFaq';

export interface FaqItem {
  question: string;
  answer: string;
  link?: { to: string; label: string };
  defaultOpen?: boolean;
}

const FaqRow: React.FC<FaqItem> = ({ question, answer, link, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentId = useId();

  return (
    <div className="border-b border-line last:border-0">
      <button
        className="w-full flex justify-between items-center gap-4 py-5 text-left"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls={contentId}
      >
        <h3 className="text-base md:text-[17px] font-bold text-fg leading-snug">{question}</h3>
        {isOpen ? (
          <ChevronUp className="text-accent-soft h-5 w-5 flex-shrink-0" />
        ) : (
          <ChevronDown className="text-accent-soft h-5 w-5 flex-shrink-0" />
        )}
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={contentId}
            role="region"
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className={`text-fg-muted leading-[1.6] max-w-[60ch] ${link ? 'pb-2' : 'pb-5'}`}>{answer}</p>
            {link && (
              <RouterLink
                to={link.to}
                className="mb-5 inline-flex min-h-11 items-center text-sm text-accent-soft underline-offset-4 hover:underline"
              >
                {link.label}
              </RouterLink>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface FaqProps {
  showLink?: boolean;
  showAllQuestions?: boolean;
  /** Si fourni, remplace la sélection de l'accueil. */
  items?: FaqItem[];
  /** Destination du bouton de contact. */
  contactTo?: string;
  onContactClick?: () => void;
}

const Faq: React.FC<FaqProps> = ({ showLink = true, showAllQuestions = false, items, contactTo = '/contact', onContactClick }) => {
  const allFaqItems: FaqItem[] = HOME_FAQ_ITEMS;
  
  // Liste réduite de 6 questions pour la page d'accueil
  const shortFaqItems: FaqItem[] = HOME_FAQ_SHORT;
  
  // Choisir quelle liste afficher
  const faqItems = items ?? (showAllQuestions ? allFaqItems : shortFaqItems);

  return (
    <section id="faq" className="section-spacing bg-canvas border-t border-line">
      <div className="container-site">
        <motion.div
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
            <HelpCircle className="text-accent-soft h-7 w-7 shrink-0" />
            <h2 className="heading-section">Foire Aux <span className="text-accent-soft">Questions</span></h2>
          </div>
          <p className="section-intro mb-10 text-center">
            Les réponses aux questions qu'on me pose le plus souvent. Si vous ne trouvez pas ce que vous cherchez,
            n'hésitez pas à me contacter directement.
          </p>
          
          <div className="card overflow-hidden">
            <div className="px-5 md:px-6 space-y-0">
              {faqItems.map((item, index) => (
                <FaqRow
                  key={item.question}
                  question={item.question}
                  answer={item.answer}
                  link={item.link}
                  defaultOpen={index === 0}
                />
              ))}
            </div>
          </div>
          
          <div className="mt-10 text-center">
            <p className="text-fg-muted mb-4">
              {showLink ? (
                <><RouterLink to="/contact" className="text-accent-soft hover:underline">D'autres questions ?</RouterLink> Contactez-moi directement :</>
              ) : (
                <>D'autres questions ? Contactez-moi directement :</>
              )}
            </p>
            <RouterLink
              to={contactTo}
              onClick={onContactClick}
              className="btn-primary"
            >
              <span className="flex items-center">
                Me contacter
              </span>
            </RouterLink>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Faq;